import React, { memo, useCallback, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import ToolTip from 'design-system/ToolTip';
import { Button, CardView, rem, Spacer } from 'design-system';
import { formatAmount } from 'utils';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { IsaAllowanceDescription, IsaNoAllowanceLeftDescription, } from 'features/invest/constants';
import { selectIsaAllowance } from '../selectors';
import { fetchIsaAllowance } from '../actions';
import { isWeb } from '../../../constants';
// TODO(WEA-3754): replace with final tooltip copy
const ISA_ALLOWANCE_TOOLTIP_TITLE = 'ISA allowance';
const NO_ISA_ALLOWANCE_LEFT_TITLE = 'You’ve reached your annual allowance';
const toPercent = (value, total) => total > 0
    ? `${Math.min(Math.max((value / total) * 100, 0), 100)}%`
    : '0%';
const ISAAllowanceInfoContent = ({ fromPots, fromInvest, isaAllowance, }) => {
    const styles = useStyles(styleSet);
    const { navigateToTab } = useTabNavigation();
    const { annualAllowance, totalAllowanceLeft, usedByCashIsa, usedByStocksAndSharesIsa, stocksAndSharesIsaStatus, cashIsaStatus, } = isaAllowance;
    const allowanceLeft = Math.max(0, totalAllowanceLeft);
    const hasAllowanceLeft = allowanceLeft > 0;
    // A closed S&S ISA can still have used allowance this tax year
    const includesStocksAndSharesIsa = fromInvest ||
        stocksAndSharesIsaStatus === 'ACTIVE' ||
        usedByStocksAndSharesIsa > 0;
    const totalUsed = usedByCashIsa + usedByStocksAndSharesIsa;
    const includesCashIsa = fromPots || cashIsaStatus !== 'NOT_OPENED' || usedByCashIsa > 0;
    const handleIsaSetup = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://invest/account', 'Set up investment account');
            return;
        }
        navigateToTab('Invest');
    }, [navigateToTab]);
    const handleCashIsaSetup = useCallback(() => {
        navigateToTab('Save', { tab: 'pots' });
    }, [navigateToTab]);
    return (<CardView withPadding noMargin>
      <View style={staticStyles.row}>
        <View style={staticStyles.titleContainer}>
          <Text Text-16>ISA allowance left</Text>
          <Spacer w={8}/>
          <ToolTip small colorKey="secondary" title={hasAllowanceLeft
            ? ISA_ALLOWANCE_TOOLTIP_TITLE
            : NO_ISA_ALLOWANCE_LEFT_TITLE} subtitle={hasAllowanceLeft
            ? IsaAllowanceDescription
            : IsaNoAllowanceLeftDescription}/>
        </View>
        <Text Numbers-16>{formatAmount(allowanceLeft, 'GBP', 2)}</Text>
      </View>
      <Spacer h={12}/>
      <View style={[staticStyles.bar, styles.barTrack]}>
        {includesStocksAndSharesIsa ? (<View style={[
                staticStyles.bar,
                staticStyles.barSegment,
                styles.stocksAndSharesIsaColor,
                { width: toPercent(totalUsed, annualAllowance) },
            ]}/>) : null}
        <View style={[
            staticStyles.bar,
            staticStyles.barSegment,
            styles.cashIsaColor,
            { width: toPercent(usedByCashIsa, annualAllowance) },
        ]}/>
      </View>
      <Spacer h={16}/>
      <View style={staticStyles.bodyRow}>
        <View style={staticStyles.labelContainer}>
          <View style={[staticStyles.dot, styles.cashIsaColor]}/>
          <Spacer w={8}/>
          <Text TextThin-14 Secondary>
            Cash ISA
          </Text>
        </View>
        {includesCashIsa ? (<Text NumbersThin-14 Secondary>
            {formatAmount(usedByCashIsa, 'GBP', 2)}
          </Text>) : (<Button hug small square brandReversed title="Set up" onPress={handleCashIsaSetup}/>)}
      </View>
      <Spacer h={4}/>
      <View style={staticStyles.bodyRow}>
        <View style={staticStyles.labelContainer}>
          <View style={[staticStyles.dot, styles.stocksAndSharesIsaColor]}/>
          <Spacer w={8}/>
          <Text TextThin-14 Secondary>
            Stocks & Shares ISA
          </Text>
        </View>
        {includesStocksAndSharesIsa ? (<Text NumbersThin-14 Secondary>
            {formatAmount(usedByStocksAndSharesIsa, 'GBP', 2)}
          </Text>) : (<Button hug small square brandReversed title="Set up" onPress={handleIsaSetup}/>)}
      </View>
    </CardView>);
};
const ISAAllowanceInfo = ({ fromPots, fromInvest }) => {
    const dispatch = useAppDispatch();
    const isaAllowance = useAppSelector(selectIsaAllowance);
    useEffect(() => {
        dispatch(fetchIsaAllowance());
    }, []);
    if (!isaAllowance || isaAllowance.allowanceUnavailable) {
        return null;
    }
    return (<ISAAllowanceInfoContent fromPots={fromPots} fromInvest={fromInvest} isaAllowance={isaAllowance}/>);
};
export default memo(ISAAllowanceInfo);
const styleSet = createStyleSheets((colors) => ({
    barTrack: {
        backgroundColor: colors.background.dark,
    },
    cashIsaColor: {
        backgroundColor: colors.elements.save,
    },
    stocksAndSharesIsaColor: {
        backgroundColor: colors.elements.investment,
    },
}));
const staticStyles = StyleSheet.create({
    bodyRow: {
        paddingVertical: rem(4),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    titleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 1,
    },
    labelContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 1,
    },
    bar: {
        height: rem(4),
        borderRadius: rem(100),
    },
    barSegment: {
        position: 'absolute',
        top: 0,
        left: 0,
    },
    dot: {
        width: rem(8),
        height: rem(8),
        borderRadius: rem(4),
    },
});
